import type { CompetitionDetail, Registration, Student } from "../types/database";
import { supabase } from "./supabase";

function norm(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

function inList(value: string | null, list: string[] | null): boolean {
  if (!list || list.length === 0) return true;
  const v = norm(value);
  if (!v) return false;
  // Exact normalized match only. Fuzzy substring matching previously let
  // "MIT" match "MITigate University" or "Stanford" match "Stanford-Brown
  // Joint Program" unintentionally - too loose for eligibility gating.
  return list.some((x) => norm(x) === v);
}

/**
 * Client-side eligibility vs competition filters (school / major / grad year).
 */
export function isEligibleForCompetition(student: Student | null, competition: CompetitionDetail): boolean {
  return getEligibilityIssues(student, competition).length === 0;
}

export type EligibilityIssue =
  | { kind: "no_profile"; reason: string; fixHint: string }
  | { kind: "school"; reason: string; fixHint: string; allowed: string[] }
  | { kind: "major"; reason: string; fixHint: string; allowed: string[] }
  | { kind: "grad_year_missing"; reason: string; fixHint: string }
  | { kind: "grad_year_out_of_range"; reason: string; fixHint: string; min: number | null; max: number | null };

/**
 * Plain-English list of reasons a student isn't eligible. Empty array means
 * fully eligible. Use to drive the UI explanation pill on case cards.
 */
export function getEligibilityIssues(
  student: Student | null,
  competition: CompetitionDetail
): EligibilityIssue[] {
  const issues: EligibilityIssue[] = [];

  if (!student) {
    issues.push({
      kind: "no_profile",
      reason: "Sign in to see if you're eligible",
      fixHint: "Complete your student profile to check eligibility.",
    });
    return issues;
  }

  const schools = competition.eligibility_schools;
  if (schools && schools.length > 0 && !inList(student.university, schools)) {
    const studentSchool = (student.university ?? "").trim();
    issues.push({
      kind: "school",
      allowed: schools,
      reason: studentSchool
        ? `Open to ${humanList(schools)} only — your profile lists ${studentSchool}.`
        : `Open to ${humanList(schools)} only — add your university to your profile.`,
      fixHint: studentSchool
        ? "If your school name is recorded differently than the host posted it, contact support."
        : "Add your university to your profile.",
    });
  }

  const majors = competition.eligibility_majors;
  if (majors && majors.length > 0 && !inList(student.major, majors)) {
    const studentMajor = (student.major ?? "").trim();
    issues.push({
      kind: "major",
      allowed: majors,
      reason: studentMajor
        ? `Looking for ${humanList(majors)} — your major is ${studentMajor}.`
        : `Looking for ${humanList(majors)} — add your major to your profile.`,
      fixHint: "If your major is listed under a different name, update your profile to match.",
    });
  }

  const y = student.graduation_year;
  const minY = competition.eligibility_grad_year_min;
  const maxY = competition.eligibility_grad_year_max;
  if (minY != null || maxY != null) {
    if (y == null) {
      issues.push({
        kind: "grad_year_missing",
        reason: `Open to graduation years ${formatYearRange(minY, maxY)} — add your year to your profile.`,
        fixHint: "Set your graduation year on your profile.",
      });
    } else if ((minY != null && y < minY) || (maxY != null && y > maxY)) {
      issues.push({
        kind: "grad_year_out_of_range",
        min: minY,
        max: maxY,
        reason: `Open to graduation years ${formatYearRange(minY, maxY)} — your year is ${y}.`,
        fixHint: "Update your graduation year if it's wrong on your profile.",
      });
    }
  }

  return issues;
}

function humanList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function formatYearRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return min === max ? `${min}` : `${min}–${max}`;
  if (min != null) return `${min}+`;
  if (max != null) return `up to ${max}`;
  return "any";
}

export async function getMyRegistration(competitionId: string): Promise<Registration | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return null;

  const { data, error } = await supabase
    .from("registrations")
    .select("*")
    .eq("competition_id", competitionId)
    .eq("student_id", userData.user.id)
    .maybeSingle();

  if (error || !data) return null;
  return data as Registration;
}

export async function registerForCompetition(competitionId: string): Promise<{ error: Error | null }> {
  // Server-side enforcement: register_for_competition RPC validates
  // every eligibility dimension (schools / majors / grad-year range /
  // verification gate / enrollment cap) and raises with a human-readable
  // message. The previous client-side `isEligibleForCompetition` check
  // is kept as a UI hint via `getEligibilityIssues` — exported above —
  // but the authoritative gate lives in the RPC so direct API calls
  // can't bypass it.
  const { error } = await supabase.rpc("register_for_competition", {
    p_competition_id: competitionId,
  });

  if (error) return { error: new Error(error.message) };
  void import("./analytics").then(({ track }) =>
    track("student_registered_for_competition", { competition_id: competitionId })
  );
  return { error: null };
}

export async function withdrawRegistration(competitionId: string): Promise<{ error: Error | null }> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    return { error: new Error(userError?.message ?? "Not signed in") };
  }

  const reg = await getMyRegistration(competitionId);
  if (!reg?.id) return { error: new Error("No registration to withdraw.") };

  const { error } = await supabase.from("registrations").update({ status: "withdrawn" }).eq("id", reg.id);
  if (error) return { error: new Error(error.message) };
  return { error: null };
}
